/**
 * Phase 23 (Plan 23-02) — SiteCompletionWidget.
 *
 * Per-site approved-day completion % for the current calendar month. Admin /
 * Site Head only (sites.read AND logsheets.read — server-gated; this widget
 * just renders whatever the API returns).
 */

import type { JSX } from 'react';

import {
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetSkeleton,
} from '@/components/dashboard/widget-states';
import type { ApiError } from '@/lib/api/error';
import type { SiteCompletionBlock, SiteCompletionRow } from '@/types/dashboard';

export interface SiteCompletionWidgetProps {
  readonly data: SiteCompletionBlock | null | undefined;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  readonly onRetry: () => void;
  readonly className?: string;
}

interface RowProps {
  readonly row: SiteCompletionRow;
}

function SiteRow({ row }: RowProps): JSX.Element {
  // Clamp percent to [0,100] for the bar's visual width regardless of any server slop.
  const clamped = Math.max(0, Math.min(100, row.percent));
  return (
    <div
      className="space-y-1"
      data-testid={`site-completion-row-${String(row.siteId)}`}
    >
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{row.siteName}</span>
        <span className="text-muted-foreground tabular-nums">
          {String(row.approvedDays)}/{String(row.expectedDays)} ·{' '}
          <span data-testid={`site-completion-pct-${String(row.siteId)}`}>
            {String(Math.round(row.percent))}%
          </span>
        </span>
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
        <div
          className="bg-chart-2 h-full rounded-full"
          style={{ width: `${String(clamped)}%` }}
          data-testid={`site-completion-bar-${String(row.siteId)}`}
        />
      </div>
    </div>
  );
}

interface BodyProps {
  readonly data: SiteCompletionBlock;
}

function SiteCompletionBody({ data }: BodyProps): JSX.Element {
  return (
    <div className="space-y-3" data-testid="site-completion-list">
      {data.rows.map((row) => (
        <SiteRow key={row.siteId} row={row} />
      ))}
    </div>
  );
}

export function SiteCompletionWidget({
  data,
  isLoading,
  error,
  onRetry,
  className,
}: SiteCompletionWidgetProps): JSX.Element {
  return (
    <WidgetCard
      title="Site Completion (this month)"
      testId="site-completion-widget"
      {...(className !== undefined ? { className } : {})}
    >
      {isLoading ? (
        <WidgetSkeleton testId="site-completion-skeleton" rows={3} />
      ) : error ? (
        <WidgetError
          error={error}
          onRetry={onRetry}
          testId="site-completion-error"
        />
      ) : !data || data.rows.length === 0 ? (
        <WidgetEmpty
          message="No site completion data yet."
          testId="site-completion-empty"
        />
      ) : (
        <SiteCompletionBody data={data} />
      )}
    </WidgetCard>
  );
}
